import React from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material/";
import { loginRequest, b2cPolicies } from "../config/authConfig";
import config from "../config";
import { strings } from "../common/i18n";
import useBranding from "../common/hooks/use-branding";
import styles from "./styles";
import { logToSentry } from "../config/Sentry";

/**
 * Azure B2C Login Component
 * Shows a message screen with manual login button
 */

const AzureB2CLogin = () => {
    const { instance, accounts, inProgress } = useMsal();
    const navigate = useNavigate();
    const branding = useBranding();
    const loginLogo = branding?.image?.loginLogo;

    // Track if user is actively trying to log in (prevent auto-redirect during login process)
    const [isLoggingIn, setIsLoggingIn] = React.useState(false);

    const isAuthenticated = accounts.length > 0;

    // Check for specific messages
    const isExpired = localStorage.getItem("session_expired") === "true";
    const isLoggedOut = localStorage.getItem("user_logged_out") === "true";
    const hasBackendError =
        localStorage.getItem("backend_auth_failed") === "true";
    const passwordResetSuccess =
        localStorage.getItem("password_reset_success") === "true";

    // If session expired, clear MSAL cache immediately on component mount
    // BUT: Don't clear if we're in the middle of a login redirect
    React.useEffect(() => {
        if (isExpired && accounts.length > 0 && inProgress === "none") {
            instance.clearCache().catch((error) => {
                logToSentry("Failed to clear MSAL cache on mount", {
                    error: {
                        name: error?.name,
                        message: error?.message,
                        errorCode: error?.errorCode
                    }
                });
            });
        }
    }, [isExpired, instance, accounts.length, inProgress]);

    // If already authenticated, navigate to protected routes
    // BUT: Don't redirect if user just logged out, session expired, or actively logging in
    React.useEffect(() => {
        if (
            isAuthenticated &&
            inProgress === "none" &&
            !isLoggedOut &&
            !hasBackendError &&
            !isExpired &&
            !passwordResetSuccess &&
            !isLoggingIn // Don't auto-redirect if user is actively trying to log in
        ) {
            navigate(`/${config.paths.auth}`, { replace: true });
        }
    }, [
        isAuthenticated,
        inProgress,
        isLoggedOut,
        hasBackendError,
        isExpired,
        passwordResetSuccess,
        isLoggingIn,
        accounts.length
    ]);

    // Handle manual login button click
    const handleLoginClick = async () => {
        // If there's a backend error, we need to logout from B2C completely
        // because the user is still authenticated with B2C but not with our backend
        if (hasBackendError) {
            // Clear backend error flags
            localStorage.removeItem("backend_auth_failed");
            localStorage.removeItem("backend_auth_error");
            localStorage.removeItem("password_reset_success");

            // Perform full B2C logout to clear server-side session
            // This will redirect to B2C logout, then back to our login page
            instance
                .logoutRedirect({
                    postLogoutRedirectUri: `${window.location.origin}/${config.paths.login}`
                })
                .catch((error) => {
                    logToSentry(
                        "B2C logout failed during backend error recovery",
                        {
                            error: {
                                name: error?.name,
                                message: error?.message,
                                errorCode: error?.errorCode
                            }
                        }
                    );
                    // Fallback: clear cache and redirect
                    instance.clearCache();
                    window.location.href = `/${config.paths.login}`;
                });
            return;
        }

        // Normal login flow for expired/logged out sessions
        setIsLoggingIn(true);

        // Clear all relevant flags before login
        localStorage.removeItem("user_logged_out");
        localStorage.removeItem("backend_auth_failed");
        localStorage.removeItem("backend_auth_error");
        localStorage.removeItem("password_reset_success");

        // Clear MSAL cache completely
        try {
            await instance.clearCache();
        } catch (clearError) {
            logToSentry("Failed to clear MSAL cache in login flow", {
                error: {
                    name: clearError?.name,
                    message: clearError?.message,
                    errorCode: clearError?.errorCode
                }
            });
        }

        // Verify accounts are cleared - if not, use logoutRedirect for complete cleanup
        const remainingAccounts = instance.getAllAccounts();
        if (remainingAccounts.length > 0) {
            try {
                await instance.logoutRedirect({
                    postLogoutRedirectUri: `${window.location.origin}/${config.paths.login}`
                });
                return; // logoutRedirect will handle the full flow
            } catch (logoutError) {
                logToSentry("Logout redirect failed after cache clear", {
                    error: {
                        name: logoutError?.name,
                        message: logoutError?.message,
                        errorCode: logoutError?.errorCode
                    },
                    remainingAccountsCount: remainingAccounts.length
                });
                // Continue with normal flow
            }
        }

        // Small delay to ensure cache is fully cleared
        await new Promise((resolve) => setTimeout(resolve, 200));

        // Clear session_expired flag BEFORE redirecting to prevent cache clearing on return
        localStorage.removeItem("session_expired");

        // Redirect to Azure B2C (allow SSO and refresh token usage)
        instance.loginRedirect(loginRequest).catch((error) => {
            setIsLoggingIn(false); // Reset flag on error

            // Handle B2C password reset flow
            if (
                error.errorMessage &&
                error.errorMessage.indexOf("AADB2C90118") > -1
            ) {
                instance.loginRedirect({
                    authority: b2cPolicies.resetPassword,
                    scopes: loginRequest.scopes,
                    extraQueryParameters: loginRequest.extraQueryParameters
                });
            } else {
                // Log other login redirect errors to Sentry
                logToSentry("Login redirect failed", {
                    error: {
                        name: error?.name,
                        message: error?.message,
                        errorCode: error?.errorCode,
                        errorMessage: error?.errorMessage
                    },
                    isExpired,
                    isLoggedOut,
                    hasBackendError
                });
            }
        });
    };

    // Determine message to show
    let messageTitle = strings("sessionEnded") || "Session Ended";
    let messageBody =
        strings("messages.pleaseLoginAgain") ||
        "Please click the button below to log in again.";

    if (passwordResetSuccess) {
        messageTitle =
            strings("passwordResetSuccess") || "Password Reset Successful";
        messageBody =
            strings("messages.passwordResetSuccessMessage") ||
            "Your password has been reset successfully. Please login with your new password.";
    } else if (isExpired) {
        messageTitle = strings("sessionExpired") || "Session Expired";
        messageBody =
            strings("messages.sessionExpiredMessage") ||
            "Your session has expired. Please log in again.";
    } else if (isLoggedOut) {
        messageTitle = strings("loggedOut") || "Logged Out";
        messageBody =
            strings("messages.loggedOutMessage") ||
            "You have been logged out. Please log in again.";
    } else if (hasBackendError) {
        messageTitle = strings("authenticationError") || "Authentication Error";
        messageBody =
            strings("messages.authErrorAccountNotFound") ||
            "No account found for this user. Please log in again.";
    }

    // Show message screen with login button
    return (
        <Box sx={styles.container}>
            <Box sx={styles.messageBox}>
                {loginLogo && (
                    <Box
                        component="img"
                        src={loginLogo}
                        alt="Logo"
                        sx={styles.logo}
                    />
                )}
                <Typography variant="h5" gutterBottom>
                    {messageTitle}
                </Typography>
                <Typography variant="body1" sx={styles.message}>
                    {messageBody}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLoginClick}
                    sx={styles.loginButton}
                    disabled={inProgress !== "none"}
                >
                    {inProgress !== "none"
                        ? strings("redirecting") || "Redirecting..."
                        : hasBackendError
                        ? strings("backToLogin") || "Back to Login"
                        : strings("loginAgain") || "Login Again"}
                </Button>
                {hasBackendError && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ mt: 2 }}
                    >
                        {strings("messages.authErrorContactSupport") ||
                            "If you continue to experience issues, please contact support."}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default AzureB2CLogin;
